import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  McCard,
  McLoadingIndicator,
  McNotification,
  McToast,
  McButton,
  McIcon
} from '@maersk-global/mds-react-wrapper/components-core';
import { roleConfig } from '../config/roles';

// Components
import { QueryPageHeader } from '../components/queries/QueryPageHeader';
import { QueryListToolbar } from '../components/queries/QueryListToolbar';
import { QueryTable } from '../components/queries/QueryTable';
import { QueryCategories } from '../components/queries/QueryCategories';
import { CreateVersionDialog } from '../components/queries/dialogs/CreateVersionDialog';
import { QueryHistoryDialog } from '../components/queries/dialogs/QueryHistoryDialog';
import { SqlViewerDialog } from '../components/queries/dialogs/SqlViewerDialog';
import { SchemaExplorer } from '../components/SchemaExplorer';

// Hooks
import { useQueriesPageController } from '../hooks/useQueriesPageController';

export default function QueriesPage() {
  const navigate = useNavigate();
  const [schemaExplorerOpen, setSchemaExplorerOpen] = React.useState(false);

  const {
    // State
    searchQuery, setSearchQuery,
    tagSearchQuery, setTagSearchQuery,
    statusFilter, setStatusFilter,
    favoriteOnly, setFavoriteOnly,
    selectedTags, setSelectedTags,
    selectedCategory, setSelectedCategory,
    selectedQueryId, setSelectedQueryId,
    selectedApplicationId,
    accessibleApplications,
    selectedQuery,
    versionDialogOpen,
    historyDialogOpen,
    sqlViewerOpen,
    loadingVersionNumber,
    loadingHistory,
    versionForm, setVersionForm,
    versionHistory,
    selectedVersionSql,
    selectedVersionInfo,
    dialogError,
    snackbarOpen, setSnackbarOpen,
    snackbarMessage,
    snackbarSeverity,

    // Data
    queries,
    totalElements,
    isLoading,
    error,
    isFetchingNextPage,
    categories,
    categoryCounts,
    schedules,

    // Permissions
    canCreateVersions,
    canActivateQueries,

    // Handlers
    fetchNextPage,
    hasNextPage,
    handleQuerySelect,
    handleApplicationChange,
    handleCreateVersion,
    handleVersionSubmit,
    handleVersionDialogClose,
    handleViewVersionHistory,
    handleSetCurrentVersion,
    handleHistoryDialogClose,
    handleViewSql,
    handleSqlViewerClose,

    // Mutations
    toggleActivation,
    createVersionMutation,
    setCurrentVersionMutation
  } = useQueriesPageController();

  // Intersection Observer for Infinite Scroll
  const observerTarget = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);


  return (
    <>
      <QueryPageHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        tagSearchQuery={tagSearchQuery}
        setTagSearchQuery={setTagSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        favoriteOnly={favoriteOnly}
        setFavoriteOnly={setFavoriteOnly}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        selectedApplicationId={selectedApplicationId}
        setSelectedApplicationId={handleApplicationChange}
        accessibleApplications={accessibleApplications}
        onNavigate={navigate}
        onOpenSchemaExplorer={() => setSchemaExplorerOpen(true)}
      />

      {error && (
        <div style={{ backgroundColor: '#fdecea', color: '#d32f2f', padding: '12px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #f5c6cb', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <McIcon icon="times-circle" />
          <span>Error loading queries: {error instanceof Error ? error.message : 'Unknown error'}</span>
        </div>
      )}
      {toggleActivation.isError && (
        <div style={{ backgroundColor: '#fdecea', color: '#d32f2f', padding: '12px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #f5c6cb', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <McIcon icon="times-circle" />
          <span>
            {(toggleActivation.error as any)?.response?.status === 409
              ? "Cannot deactivate query because it has active schedules. Please disable schedules first."
              : "Failed to update query activation. Please try again."}
          </span>
        </div>
      )}

      <McCard style={{ overflow: 'visible' }}>
        <QueryCategories
          categories={categories}
          categoryCounts={categoryCounts}
          selectedCategory={selectedCategory}
          onSelectCategory={(category) => {
            setSelectedCategory(category);
            setSelectedQueryId(null);
          }}
        />

        <QueryListToolbar
          selectedQueryId={selectedQueryId}
          queries={queries}
          canCreateVersions={canCreateVersions}
          canActivateQueries={canActivateQueries}
          onViewHistory={handleViewVersionHistory}
          onToggleActivation={(query) => {
            const isActive = query.active ?? query.isActive ?? false;
            toggleActivation.mutate({
              queryId: query.queryId,
              nextActive: !isActive,
            });
          }}
          onCreateVersion={handleCreateVersion}
          activationPending={toggleActivation.isPending}
        />

        <QueryTable
          queries={queries}
          totalElements={totalElements}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          selectedQueryId={selectedQueryId}
          searchQuery={searchQuery}
          onSelectQuery={handleQuerySelect}
          onNavigate={navigate}
          observerRef={observerTarget}
        />
      </McCard>

      {/* Dialogs */}
      <CreateVersionDialog
        open={versionDialogOpen}
        onClose={handleVersionDialogClose}
        selectedQuery={selectedQuery}
        schedules={schedules || []}
        dialogError={dialogError}
        loadingVersionNumber={loadingVersionNumber}
        versionForm={versionForm}
        setVersionForm={setVersionForm}
        onSubmit={handleVersionSubmit}
        isPending={createVersionMutation.isPending}
      />

      <QueryHistoryDialog
        open={historyDialogOpen}
        onClose={handleHistoryDialogClose}
        selectedQuery={selectedQuery}
        history={versionHistory}
        loading={loadingHistory}
        canCreateVersions={canCreateVersions}
        onViewSql={handleViewSql}
        onSetCurrentVersion={handleSetCurrentVersion}
        isSettingCurrentVersion={setCurrentVersionMutation.isPending}
        onCreateVersion={() => {
          if (selectedQuery) handleCreateVersion(selectedQuery);
        }}
      />

      <SqlViewerDialog
        open={sqlViewerOpen}
        onClose={handleSqlViewerClose}
        versionInfo={selectedVersionInfo}
        sqlContent={selectedVersionSql}
        canCreateVersions={canCreateVersions}
        onCreateVersion={() => {
          if (selectedQuery) handleCreateVersion(selectedQuery);
        }}
      />

      {/* Success/Error Notification */}
      <McToast
        open={snackbarOpen}
        duration={5000}
        appearance={snackbarSeverity === 'error' ? 'error' : (snackbarSeverity === 'success' ? 'success' : 'info')}
        close={() => setSnackbarOpen(false)}
        position="bottom-right"
        style={{ zIndex: 9999, position: 'fixed' } as any}
      >
        <McNotification
          heading={snackbarSeverity === 'error' ? 'Error' : 'Success'}
          body={snackbarMessage}
        />
      </McToast>

      <SchemaExplorer
        open={schemaExplorerOpen}
        onClose={() => setSchemaExplorerOpen(false)}
        terminalId=""
      />
    </>
  );
}
